/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-11
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, ShieldCheck, FileText } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import { buildLegal } from '../../lib/legal';
import { PageHero } from '../components/Section';
import { SHELL, SECTION, EYEBROW_LIGHT, H3, CARD, BTN_PRIMARY, BTN_ON_LIGHT } from '../theme';

/**
 * Pages juridiques du site Tchad : confidentialite et conditions d'utilisation.
 *
 * Meme composant pour les deux documents, meme raison qu'au UK : la maquette
 * est commune, seul le contenu change. Les textes viennent de `lib/legal.js`,
 * ou le gabarit est rempli avec la fiche de l'entite tchadienne — adresse de
 * N'Djamena, droit tchadien, autorite de controle locale.
 *
 * La mise en page reprend `PageHero` et les jetons de `theme.js` plutot que de
 * redefinir ses propres cartes : c'est precisement ce que la refonte du site
 * avait supprime.
 */
export const Legal = ({ doc }) => {
  const { language, t } = useLanguage();
  const legal = buildLegal(doc, 'tchad', language);
  const body = useReveal();
  if (!legal) return null;

  const Icon = doc === 'privacy' ? ShieldCheck : FileText;
  const sibling = buildLegal(doc === 'privacy' ? 'terms' : 'privacy', 'tchad', language);

  return (
    <main data-testid={`tchad-legal-${doc}-page`} className="flex-1">
      <SEO
        title={`${legal.title} — Prenim Tchad`}
        description={legal.lead.slice(0, 160)}
        lang={language}
      />

      <PageHero eyebrow={legal.kindLabel} title={legal.title} lead={legal.lead}>
        <p className="no-justify inline-flex items-center gap-2 rounded-full border border-amber-400/40 px-4 py-2 text-sm text-amber-300">
          <Icon className="h-4 w-4" />
          {legal.updatedLabel} : <time dateTime={legal.updated}>{legal.updated}</time>
        </p>
      </PageHero>

      <section className={`${SECTION} bg-white`}>
        <div className={`${SHELL} grid gap-12 lg:grid-cols-12 lg:gap-16`}>
          {/* Sommaire. Sous `lg` il passe au-dessus du texte : en colonne
              laterale il serait trop etroit pour rester lisible. */}
          <aside className="lg:col-span-4">
            <nav
              aria-label={legal.tocLabel}
              className="rounded-lg border border-slate-200 bg-slate-50 p-6 lg:sticky lg:top-28"
            >
              <h2 className={EYEBROW_LIGHT}>{legal.tocLabel}</h2>
              <ol className="mt-5 space-y-3">
                {legal.sections.map((s, i) => (
                  <li key={s.key}>
                    <a
                      href={`#${s.key}`}
                      className="group flex gap-3 text-sm text-slate-600 transition-colors hover:text-amber-600"
                    >
                      <span className="font-mono text-xs text-slate-400 transition-colors group-hover:text-amber-600">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-start">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <div ref={body.ref} className={`${body.className} lg:col-span-8`}>
            <div className="space-y-12">
              {legal.sections.map((s, i) => (
                <article
                  key={s.key}
                  id={s.key}
                  data-testid={`tchad-legal-section-${s.key}`}
                  className="scroll-mt-28 border-s-2 border-amber-500/30 ps-6"
                >
                  <span className="font-mono text-xs font-semibold text-amber-600">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h2 className={`${H3} mt-2 text-slate-950`}>{s.title}</h2>
                  <p className="mt-4 leading-relaxed text-slate-700">{s.body}</p>
                </article>
              ))}
            </div>

            {/* Un document juridique doit se terminer sur une adresse a qui
                ecrire, et non sur son dernier article. */}
            <div className={`${CARD} mt-16 overflow-hidden !translate-y-0`}>
              <img
                src="/images/hero-tchad.webp"
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="h-40 w-full object-cover sm:h-48"
              />
              <div className="bg-slate-950 p-8 text-white">
                <p className="no-justify text-sm leading-relaxed text-slate-300">
                  {legal.notice}
                </p>
                <div className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
                  <p className="no-justify flex items-start gap-2 text-slate-300">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                    <span>{legal.address}</span>
                  </p>
                  <p className="flex items-center gap-2 text-slate-300">
                    <Mail className="h-4 w-4 shrink-0 text-amber-400" />
                    <a href={`mailto:${legal.entity.email}`} className="hover:text-amber-400">
                      {legal.entity.email}
                    </a>
                  </p>
                  <p className="flex items-center gap-2 text-slate-300">
                    <Phone className="h-4 w-4 shrink-0 text-amber-400" />
                    <a
                      href={`tel:${legal.entity.telephone.replace(/\s/g, '')}`}
                      className="hover:text-amber-400"
                    >
                      {legal.entity.telephone}
                    </a>
                  </p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to={`/tchad/${doc === 'privacy' ? 'terms' : 'privacy'}`}
                    data-testid="tchad-legal-sibling-link"
                    className={`${BTN_ON_LIGHT} !border-white/30 !px-5 !py-2.5 !text-sm !text-white hover:!border-amber-400 hover:!bg-transparent hover:!text-amber-400`}
                  >
                    {sibling.title}
                  </Link>
                  <Link to="/tchad/contact" className={`${BTN_PRIMARY} !px-5 !py-2.5 !text-sm`}>
                    {t.nav.contact}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
